import { logging, API_BASE_URL } from '@/config/env';
import type {
  EmailTemplate,
  EmailTemplateListResponse,
  ApiEmailTemplateListResponse,
  EmailTemplateQueryParams,
} from './emailTemplate.dto';

import { redirectToLogin } from '../../utils/sessionExpired';
// Custom logging function
const customLog = (...args: unknown[]) => {
  if (logging) {
    console.log(...args);
  }
};

class EmailTemplateService {
  private baseUrl: string;
  private logoutCallback: (() => void) | null = null;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  setLogoutCallback(callback: () => void) {
    this.logoutCallback = callback;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseUrl}${endpoint}`;
    customLog('=== API Request ===');
    customLog('URL:', url);
    customLog('Method:', options.method || 'GET');

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    customLog('Response status:', response.status);
    customLog('Response ok:', response.ok);

    if (!response.ok) {
      const errorText = await response.text();
      customLog('Error response:', errorText);

      // Try to parse error response to check for session expired
      try {
        const errorData = JSON.parse(errorText);
        if (
          errorData.status === false &&
          errorData.message === 'Invalid or expired admin session'
        ) {
          customLog('Session expired, triggering logout');
          if (this.logoutCallback) {
            this.logoutCallback();
          }
          redirectToLogin();
        }
      } catch (parseError) {
        // If parsing fails, continue with original error handling
      }

      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    customLog('Response data:', JSON.stringify(data, null, 2));

    // Check for session expired in successful response
    if (
      data.status === false &&
      data.message === 'Invalid or expired admin session'
    ) {
      customLog('Session expired in response, triggering logout');
      if (this.logoutCallback) {
        this.logoutCallback();
      }
      redirectToLogin();
    }

    return data;
  }

  async getEmailTemplates(
    token: string,
    params: EmailTemplateQueryParams = {}
  ): Promise<EmailTemplateListResponse> {
    customLog('API Service: getEmailTemplates called with params:', params);

    const queryString = new URLSearchParams();
    if (params.page) queryString.append('page', params.page.toString());
    if (params.limit) queryString.append('limit', params.limit.toString());
    if (params.search) queryString.append('search', params.search);
    if (params.status !== undefined)
      queryString.append('status', params.status ? 'true' : 'false');
    if (params.sortBy) queryString.append('sortBy', params.sortBy);
    if (params.sortOrder) queryString.append('sortOrder', params.sortOrder);

    const endpoint = `/admin/email-templates${
      queryString.toString() ? `?${queryString.toString()}` : ''
    }`;

    customLog('API Service: Final endpoint:', endpoint);

    const response = await this.request<ApiEmailTemplateListResponse>(
      endpoint,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    customLog('=== API Service Response Analysis ===');
    customLog('Full response object:', response);
    customLog('response keys:', Object.keys(response));
    customLog('response.status:', (response as any).status);
    customLog('response.message:', (response as any).message);
    customLog('response.data:', response.data);
    customLog('response.data type:', typeof response.data);

    // Check if response.data exists
    if (!response.data) {
      customLog('API Service: Warning - response.data is undefined');
      return {
        data: [],
        meta: { total: 0, page: 1, lastPage: 1, limit: 10 },
      };
    }

    // Check if response.data has the nested structure or is flat
    const responseData = response.data as any;

    // If response.data is an array (flat structure), use response metadata
    if (Array.isArray(responseData)) {
      const result = {
        data: responseData,
        meta: {
          total: (response as any).total || 0,
          page: (response as any).page || 1,
          lastPage: (response as any).lastPage || 1,
          limit: params.limit || 10,
        },
      };

      customLog('=== Flat Structure Result ===');
      customLog('result.data length:', result.data.length);
      customLog('result.meta:', result.meta);

      return result;
    }

    // Handle nested structure
    const result = {
      data: responseData.data || responseData || [],
      meta: {
        total:
          responseData.meta?.total ||
          responseData.total ||
          (response as any).total ||
          0,
        page:
          responseData.meta?.page ||
          responseData.page ||
          (response as any).page ||
          1,
        lastPage:
          responseData.meta?.lastPage ||
          responseData.totalPages ||
          (response as any).lastPage ||
          1,
        limit: params.limit || 10,
      },
    };

    customLog('=== Final Parsed Result ===');
    customLog('result.data length:', result.data.length);
    customLog('result.meta:', result.meta);

    return result;
  }

  async getEmailTemplate(token: string, id: number): Promise<EmailTemplate> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data: EmailTemplate;
    }>(`/admin/email-templates/${id}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  }

  async createEmailTemplate(
    token: string,
    templateData: Partial<EmailTemplate>
  ): Promise<EmailTemplate> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data: EmailTemplate;
    }>('/admin/email-templates', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(templateData),
    });
    return response.data;
  }

  async updateEmailTemplate(
    token: string,
    id: number,
    templateData: Partial<EmailTemplate>
  ): Promise<EmailTemplate> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data: EmailTemplate;
    }>(`/admin/email-templates/${id}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(templateData),
    });
    return response.data;
  }

  async deleteEmailTemplate(
    token: string,
    id: number
  ): Promise<{ message: string }> {
    const response = await this.request<{
      status: boolean;
      message: string;
      data?: unknown;
    }>(`/admin/email-templates/${id}`, {
      method: 'DELETE',
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return { message: response.message };
  }

  // Status toggle not specified; implement if API supports it
}

export const emailTemplateService = new EmailTemplateService();
